import React from 'react';

import { SkyMapAxiosServiceFactory } from '../../../../services/axios/skymap-axios-service-factory';
import { isDefined } from '../../../../utils/variables';
import { AssetManager } from '../../../../viewer/asset-manager';
import { ElementObj } from '../../../../viewer/elements/element-obj';
import { Poi } from '../../../../viewer/elements/poi/poi';
import { TerrainModel } from '../../../../viewer/elements/terrain-model';
import { projectStateAdapter } from '../../../state/project-state/project-state-adapter';
// Removed unused ProjectContext import

// Filters

type CreatedAtFilter = {
  createdAtFrom?: string;
  createdAtTo?: string;
};

type ImageCapturedAtFilter = {
  imageCapturedAtFrom?: string;
  imageCapturedAtTo?: string;
};

// Category buttons in UI
export type AssetCategoryFilter = 'Design' | 'Area' | 'Line' | 'Poi' | 'CAD' | 'Markmodeller';

// Dynamic asset API types (server)
type DynamicApiType = 'Geometry' | 'Design' | 'Dxf' | 'Glb';

function mapCategoryToDynamicApiType(category?: AssetCategoryFilter): DynamicApiType[] | undefined {
  switch (category) {
    case 'Design':
      return ['Design'];
    case 'CAD':
      return ['Dxf', 'Glb'];
    case 'Area':
    case 'Line':
      return ['Geometry'];
    default:
      return undefined;
  }
}

function isAssetMatchingCategory(asset: ElementObj, category?: AssetCategoryFilter): boolean {
  if (!category) {
    return true;
  }
  if (category === 'Poi') {
    return asset instanceof Poi;
  }
  if (category === 'Design') {
    return asset.assetType === 'design';
  }
  if (category === 'CAD') {
    return asset.assetType === 'dxf' || asset.assetType === 'glb';
  }
  if (category === 'Line') {
    return asset.assetType === 'line';
  }
  if (category === 'Area') {
    return asset.assetType === 'area';
  }
  if (category === 'Markmodeller') {
    return asset.assetType === 'terrain-model';
  }
  return true;
}

function isDynamicCategory(category?: AssetCategoryFilter): boolean {
  return category === 'Design' || category === 'CAD' || category === 'Area' || category === 'Line';
}

function isPoiCategory(category?: AssetCategoryFilter): boolean {
  return category === 'Poi';
}

// Store

type FilterEntity = {
  id: string;
  title?: string;
};

type AssetFilterStore = {
  // UI state
  loading: boolean;
  error?: string;

  // Active filter inputs
  createdAt?: CreatedAtFilter;
  imageCapturedAt?: ImageCapturedAtFilter;
  createdBy?: FilterEntity;
  assetCategoryType?: AssetCategoryFilter;
  tagGroups?: string[][];
  timelineContainerId?: string;

  // Apply/clear filters
  applyCreatedAtFilter: (filter?: CreatedAtFilter) => void;
  applyImageCapturedAtFilter: (filter?: ImageCapturedAtFilter) => void;
  applyCreatedByFilter: (user?: FilterEntity) => void;
  applyAssetTypeFilter: (type?: AssetCategoryFilter) => void;
  applyTagGroupsFilter: (tagGroups?: string[][]) => void;
  applyTimelineFilter: (timelineContainerId?: string) => void;
  clearFilters: () => void;

  // Visibility helpers
  isFiltering: boolean;
  isAssetVisible: (asset: ElementObj) => boolean;
  filtersEnabled: boolean;
  toggleFiltersEnabled: () => void;
};

const noopStore: AssetFilterStore = {
  loading: false,
  createdAt: undefined,
  imageCapturedAt: undefined,
  createdBy: undefined,
  assetCategoryType: undefined,
  timelineContainerId: undefined,
  applyCreatedAtFilter: () => undefined,
  applyImageCapturedAtFilter: () => undefined,
  applyCreatedByFilter: () => undefined,
  applyAssetTypeFilter: () => undefined,
  applyTagGroupsFilter: () => undefined,
  applyTimelineFilter: () => undefined,
  clearFilters: () => undefined,
  isFiltering: false,
  isAssetVisible: () => true,
  filtersEnabled: true,
  toggleFiltersEnabled: () => undefined,
};

const AssetFilterContext = React.createContext<AssetFilterStore | undefined>(undefined);

type Props = { children: React.ReactNode };

export const AssetFilterProvider = (props: Props) => {
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState<string | undefined>(undefined);
  const [createdAt, setCreatedAt] = React.useState<CreatedAtFilter | undefined>(undefined);
  const [imageCapturedAt, setImageCapturedAt] = React.useState<ImageCapturedAtFilter | undefined>(
    undefined,
  );
  const [createdBy, setCreatedBy] = React.useState<FilterEntity | undefined>(undefined);
  const [assetCategoryType, setAssetCategoryType] = React.useState<AssetCategoryFilter | undefined>(
    undefined,
  );
  const [tagGroups, setTagGroups] = React.useState<string[][] | undefined>(undefined);
  const [timelineContainerId, setTimelineContainerId] = React.useState<string | undefined>(
    undefined,
  );
  const [filteredPoiAssetIds, setFilteredPoiAssetIds] = React.useState<Set<string> | undefined>(
    undefined,
  );
  const [filteredDynamicAssetIds, setFilteredDynamicAssetIds] = React.useState<
    Set<string> | undefined
  >(undefined);
  const [filteredTerrainGeodataIds, setFilteredTerrainGeodataIds] = React.useState<
    Set<string> | undefined
  >(undefined);
  const [filtersEnabled, setFiltersEnabled] = React.useState<boolean>(true);

  const applyCreatedAtFilter = React.useCallback((filter?: CreatedAtFilter) => {
    setCreatedAt(filter);
  }, []);

  const applyImageCapturedAtFilter = React.useCallback((filter?: ImageCapturedAtFilter) => {
    setImageCapturedAt(filter);
  }, []);

  const applyCreatedByFilter = React.useCallback((user?: FilterEntity) => {
    setCreatedBy(user);
  }, []);

  const applyAssetTypeFilter = React.useCallback((type?: AssetCategoryFilter) => {
    setAssetCategoryType(type);
  }, []);

  const applyTagGroupsFilter = React.useCallback((tagGroups?: string[][]) => {
    setTagGroups(tagGroups);
  }, []);

  const applyTimelineFilter = React.useCallback((id?: string) => {
    setTimelineContainerId(id);
  }, []);

  const clearFilters = React.useCallback(() => {
    setCreatedAt(undefined);
    setImageCapturedAt(undefined);
    setCreatedBy(undefined);
    setAssetCategoryType(undefined);
    setTagGroups(undefined);
    setTimelineContainerId(undefined);
    setFilteredPoiAssetIds(undefined);
    setFilteredDynamicAssetIds(undefined);
    setFilteredTerrainGeodataIds(undefined);
    setError(undefined);
  }, []);

  const toggleFiltersEnabled = React.useCallback(() => {
    setFiltersEnabled((v) => !v);
  }, []);

  // Fetch filtered asset ids based on createdAt/imageCapturedAt/createdBy/category
  React.useEffect(() => {
    const projectId = projectStateAdapter.getSnapshot().projectId;
    if (!projectId) {
      setFilteredPoiAssetIds(undefined);
      setFilteredDynamicAssetIds(undefined);
      setFilteredTerrainGeodataIds(undefined);
      for (const asset of AssetManager.instance.getAssets()) {
        void asset.setFilterVisibility(true);
      }
      return;
    }

    // If no filters are set, clear ids and show all
    if (
      !filtersEnabled ||
      (!createdAt?.createdAtFrom &&
        !createdAt?.createdAtTo &&
        !imageCapturedAt?.imageCapturedAtFrom &&
        !imageCapturedAt?.imageCapturedAtTo &&
        !createdBy?.id &&
        !isDefined(assetCategoryType) &&
        !(tagGroups && tagGroups.length > 0) &&
        !isDefined(timelineContainerId))
    ) {
      setFilteredPoiAssetIds(undefined);
      setFilteredDynamicAssetIds(undefined);
      setFilteredTerrainGeodataIds(undefined);
      for (const asset of AssetManager.instance.getAssets()) {
        void asset.setFilterVisibility(true);
      }
      return;
    }

    const fetchFiltered = async () => {
      setLoading(true);
      setError(undefined);
      try {
        const projectServiceV1 = SkyMapAxiosServiceFactory.instance.createProjectServiceV1();
        const hasImageCapturedAt = Boolean(
          imageCapturedAt?.imageCapturedAtFrom || imageCapturedAt?.imageCapturedAtTo,
        );
        const hasCreatedAt = Boolean(createdAt?.createdAtFrom || createdAt?.createdAtTo);
        const hasTagsActive = Boolean(tagGroups && tagGroups.length > 0);
        const hasCreatedByActive = Boolean(createdBy?.id);
        const hasTimelineActive = Boolean(timelineContainerId);
        const hasUnsupportedTerrainFilters =
          hasImageCapturedAt || hasTagsActive || hasCreatedByActive;

        if (isPoiCategory(assetCategoryType)) {
          // Only POIs
          const poiResponse = await projectServiceV1.getPois({
            path: { projectId },
            query: {
              include: [],
              createdAtFrom: createdAt?.createdAtFrom,
              createdAtTo: createdAt?.createdAtTo,
              createdById: createdBy?.id ? [createdBy.id] : undefined,
              tagGroups:
                tagGroups && tagGroups.length > 0
                  ? tagGroups.map((group) => group.join(','))
                  : undefined,
              imageCapturedAtFrom: imageCapturedAt?.imageCapturedAtFrom,
              imageCapturedAtTo: imageCapturedAt?.imageCapturedAtTo,
              timelineContainerId,
            },
          });
          const poiIds = new Set<string>();
          for (const poi of poiResponse.data) {
            const id = (poi as { id?: string }).id;
            if (id) {
              poiIds.add(id);
            }
          }
          setFilteredPoiAssetIds(poiIds);
          setFilteredDynamicAssetIds(undefined);
          setFilteredTerrainGeodataIds(undefined);
        } else if (isDynamicCategory(assetCategoryType)) {
          // If filtering by image captured date, dynamic assets should be filtered away
          if (hasImageCapturedAt) {
            setFilteredDynamicAssetIds(undefined);
            setFilteredPoiAssetIds(undefined);
            return;
          }
          // Only dynamic assets of a certain type
          const apiType = mapCategoryToDynamicApiType(assetCategoryType);
          const dynamicResponse = await projectServiceV1.getDynamicAssets({
            path: { projectId },
            query: {
              createdAtFrom: createdAt?.createdAtFrom,
              createdAtTo: createdAt?.createdAtTo,
              type: apiType,
              createdById: createdBy?.id ? [createdBy.id] : undefined,
              tagGroups:
                tagGroups && tagGroups.length > 0
                  ? tagGroups.map((group) => group.join(','))
                  : undefined,
              timelineContainerId,
            },
          });
          const dynIds = new Set<string>();
          for (const asset of dynamicResponse.data) {
            const id = (asset as { id?: string }).id;
            if (id) {
              dynIds.add(id);
            }
          }
          setFilteredDynamicAssetIds(dynIds);
          setFilteredPoiAssetIds(undefined);
          setFilteredTerrainGeodataIds(undefined);
        } else if (assetCategoryType === 'Markmodeller') {
          // Terrain models: hide if unsupported filters active; otherwise filter by createdAt or show all
          setFilteredPoiAssetIds(undefined);
          setFilteredDynamicAssetIds(undefined);
          if (hasUnsupportedTerrainFilters) {
            setFilteredTerrainGeodataIds(new Set());
          } else {
            const terrainResponse = await projectServiceV1.getTerrainModels({
              path: { projectId },
              query: {
                createdAtFrom: createdAt?.createdAtFrom,
                createdAtTo: createdAt?.createdAtTo,
                timelineContainerId,
              },
            });
            const terrainIds = new Set<string>([
              ...((terrainResponse.data as { geodataId?: string }[] | undefined) ?? [])
                .map((tm) => tm.geodataId)
                .filter((x): x is string => Boolean(x)),
            ]);
            setFilteredTerrainGeodataIds(terrainIds);
          }
        } else {
          // No category specified: fetch both (when any filter is set)
          const [poiResponse, dynamicResponse] = hasImageCapturedAt
            ? await Promise.all([
                projectServiceV1.getPois({
                  path: { projectId },
                  query: {
                    include: [],
                    createdAtFrom: createdAt?.createdAtFrom,
                    createdAtTo: createdAt?.createdAtTo,
                    createdById: createdBy?.id ? [createdBy.id] : undefined,
                    tagGroups:
                      tagGroups && tagGroups.length > 0
                        ? tagGroups.map((group) => group.join(','))
                        : undefined,
                    imageCapturedAtFrom: imageCapturedAt?.imageCapturedAtFrom,
                    imageCapturedAtTo: imageCapturedAt?.imageCapturedAtTo,
                    timelineContainerId,
                  },
                }),
                Promise.resolve({ data: [] as unknown as { id?: string }[] }),
              ])
            : await Promise.all([
                projectServiceV1.getPois({
                  path: { projectId },
                  query: {
                    include: [],
                    createdAtFrom: createdAt?.createdAtFrom,
                    createdAtTo: createdAt?.createdAtTo,
                    createdById: createdBy?.id ? [createdBy.id] : undefined,
                    tagGroups:
                      tagGroups && tagGroups.length > 0
                        ? tagGroups.map((group) => group.join(','))
                        : undefined,
                    imageCapturedAtFrom: imageCapturedAt?.imageCapturedAtFrom,
                    imageCapturedAtTo: imageCapturedAt?.imageCapturedAtTo,
                    timelineContainerId,
                  },
                }),
                projectServiceV1.getDynamicAssets({
                  path: { projectId },
                  query: {
                    createdAtFrom: createdAt?.createdAtFrom,
                    createdAtTo: createdAt?.createdAtTo,
                    createdById: createdBy?.id ? [createdBy.id] : undefined,
                    tagGroups:
                      tagGroups && tagGroups.length > 0
                        ? tagGroups.map((group) => group.join(','))
                        : undefined,
                    timelineContainerId,
                  },
                }),
              ]);
          const poiIds = new Set<string>();
          for (const poi of poiResponse.data) {
            const id = (poi as { id?: string }).id;
            if (id) {
              poiIds.add(id);
            }
          }
          setFilteredPoiAssetIds(poiIds);
          const dynIds = new Set<string>();
          for (const asset of dynamicResponse.data) {
            const id = (asset as { id?: string }).id;
            if (id) {
              dynIds.add(id);
            }
          }
          setFilteredDynamicAssetIds(dynIds);
          // Also fetch terrain models if createdAt or timeline filtering is active and no category specified.
          // If unsupported filters are active, hide all terrain models.
          if (hasUnsupportedTerrainFilters) {
            setFilteredTerrainGeodataIds(new Set());
          } else if (hasCreatedAt || hasTimelineActive) {
            const terrainResponse = await projectServiceV1.getTerrainModels({
              path: { projectId },
              query: {
                createdAtFrom: createdAt?.createdAtFrom,
                createdAtTo: createdAt?.createdAtTo,
                timelineContainerId,
              },
            });
            const terrainIds = new Set<string>([
              ...((terrainResponse.data as { geodataId?: string }[] | undefined) ?? [])
                .map((tm) => tm.geodataId)
                .filter((x): x is string => Boolean(x)),
            ]);
            setFilteredTerrainGeodataIds(terrainIds);
          } else {
            setFilteredTerrainGeodataIds(undefined);
          }
        }
      } catch (e) {
        setError(String(e));
        setFilteredPoiAssetIds(undefined);
        setFilteredDynamicAssetIds(undefined);
        setFilteredTerrainGeodataIds(undefined);
      } finally {
        setLoading(false);
      }
    };

    void fetchFiltered();
  }, [
    createdAt,
    imageCapturedAt,
    createdBy?.id,
    assetCategoryType,
    tagGroups,
    timelineContainerId,
    filtersEnabled,
  ]);

  // Apply filter visibility to scene assets whenever the filtered sets or category change
  React.useEffect(() => {
    const allAssets = AssetManager.instance.getAssets();

    // If filters are disabled, show everything and return
    if (!filtersEnabled) {
      for (const asset of allAssets) {
        void asset.setFilterVisibility(true);
      }
      return;
    }

    for (const asset of allAssets) {
      if (asset instanceof Poi) {
        // Hide POIs unless no category is selected or Poi is chosen
        if (assetCategoryType && assetCategoryType !== 'Poi') {
          void asset.setFilterVisibility(false);
          continue;
        }
        const show = !isDefined(filteredPoiAssetIds)
          ? !isDefined(assetCategoryType)
          : asset.assetId
            ? filteredPoiAssetIds.has(asset.assetId)
            : false;
        void asset.setFilterVisibility(show);
        continue;
      }

      // Non-POI assets
      // Hide if category is Poi
      if (assetCategoryType === 'Poi') {
        void asset.setFilterVisibility(false);
        continue;
      }

      // Terrain models: apply separate filtered set (by geodataId)
      if (asset instanceof TerrainModel) {
        const categoryAllowed = isAssetMatchingCategory(asset, assetCategoryType);
        if (!categoryAllowed) {
          void asset.setFilterVisibility(false);
          continue;
        }
        const show = !isDefined(filteredTerrainGeodataIds)
          ? !isDefined(assetCategoryType)
          : filteredTerrainGeodataIds.has(asset.geoDataId);
        void asset.setFilterVisibility(show);
        continue;
      }

      // Category gating for non-POI
      const categoryAllowed = isAssetMatchingCategory(asset, assetCategoryType);

      if (!categoryAllowed) {
        void asset.setFilterVisibility(false);
        continue;
      }

      // For dynamic assets (design/line/area/cad), apply id filter when present; otherwise show
      const show = !isDefined(filteredDynamicAssetIds)
        ? !isDefined(assetCategoryType)
        : asset.assetId
          ? filteredDynamicAssetIds.has(asset.assetId)
          : false;
      void asset.setFilterVisibility(show);
    }
  }, [
    filteredPoiAssetIds,
    filteredDynamicAssetIds,
    filteredTerrainGeodataIds,
    assetCategoryType,
    filtersEnabled,
  ]);

  const isAssetVisible = React.useCallback(
    (asset: ElementObj) => {
      if (!filtersEnabled) {
        return true;
      }
      if (asset instanceof Poi) {
        if (assetCategoryType && assetCategoryType !== 'Poi') {
          return false;
        }
        if (!filteredPoiAssetIds) {
          return !isDefined(assetCategoryType);
        }
        return asset.assetId ? filteredPoiAssetIds.has(asset.assetId) : false;
      }

      // Non-POI assets
      if (assetCategoryType === 'Poi') {
        return false;
      }

      if (!isAssetMatchingCategory(asset, assetCategoryType)) {
        return false;
      }

      if (asset instanceof TerrainModel) {
        if (!filteredTerrainGeodataIds) {
          return !isDefined(assetCategoryType);
        }
        return filteredTerrainGeodataIds.has(asset.geoDataId);
      }

      if (!filteredDynamicAssetIds) {
        return !isDefined(assetCategoryType);
      }
      return asset.assetId ? filteredDynamicAssetIds.has(asset.assetId) : false;
    },
    [
      filteredPoiAssetIds,
      filteredDynamicAssetIds,
      filteredTerrainGeodataIds,
      assetCategoryType,
      filtersEnabled,
    ],
  );

  const value = React.useMemo<AssetFilterStore>(
    () => ({
      loading,
      error,
      createdAt,
      imageCapturedAt,
      createdBy,
      assetCategoryType,
      tagGroups,
      timelineContainerId,
      applyCreatedAtFilter,
      applyImageCapturedAtFilter,
      applyCreatedByFilter,
      applyAssetTypeFilter,
      applyTagGroupsFilter,
      applyTimelineFilter,
      clearFilters,
      isFiltering:
        filtersEnabled &&
        (Boolean(createdAt?.createdAtFrom || createdAt?.createdAtTo) ||
          Boolean(imageCapturedAt?.imageCapturedAtFrom || imageCapturedAt?.imageCapturedAtTo) ||
          Boolean(createdBy) ||
          isDefined(assetCategoryType) ||
          Boolean(tagGroups && tagGroups.length > 0) ||
          Boolean(timelineContainerId)),
      isAssetVisible,
      filtersEnabled,
      toggleFiltersEnabled,
    }),
    [
      loading,
      error,
      createdAt,
      imageCapturedAt,
      createdBy,
      assetCategoryType,
      tagGroups,
      timelineContainerId,
      applyCreatedAtFilter,
      applyImageCapturedAtFilter,
      applyCreatedByFilter,
      applyAssetTypeFilter,
      applyTagGroupsFilter,
      applyTimelineFilter,
      clearFilters,
      isAssetVisible,
      filtersEnabled,
      toggleFiltersEnabled,
    ],
  );

  return <AssetFilterContext.Provider value={value}>{props.children}</AssetFilterContext.Provider>;
};

export function useAssetFilterStore(): AssetFilterStore {
  const ctx = React.useContext(AssetFilterContext);
  return ctx ?? noopStore;
}
